import Vue from 'vue'

export default (service) => ({
    namespaced: true,

    state: {
        picaServiceId: null,
        features: {},           // {featureKey: {enabled: true, ...params} | null}
        roles: [],              // Available roles for collaborator limits
        loading: false
    },

    getters: {
        picaServiceId: state => state.picaServiceId,

        features: state => state.features,

        loading: state => state.loading,

        /**
         * Get list of feature keys the user can configure
         * (features present in the response)
         */
        availableFeatureKeys: state => Object.keys(state.features),

        /**
         * Check if a feature is enabled
         */
        isFeatureEnabled: state => featureKey => {
            const feature = state.features[featureKey]
            return feature?.enabled === true
        },

        /**
         * Get feature configuration (or null if not configured)
         */
        getFeatureConfig: state => featureKey => state.features[featureKey] || null,

        /**
         * Get available roles for collaborator limits
         */
        roles: state => state.roles
    },

    mutations: {
        setPicaServiceId(state, id) {
            state.picaServiceId = id
        },

        setFeatures(state, features) {
            Vue.set(state, 'features', features || {})
        },

        setLoading(state, loading) {
            state.loading = loading
        },

        updateFeature(state, { featureKey, config }) {
            Vue.set(state.features, featureKey, config)
        },

        setRoles(state, roles) {
            state.roles = roles || []
        }
    },

    actions: {
        /**
         * Set the PicaService ID and reset state
         */
        setPicaServiceId({ commit }, id) {
            commit('setPicaServiceId', id)
            commit('setFeatures', {})
        },

        /**
         * Fetch features for the current PicaService
         */
        async fetchFeatures({ commit, state }) {
            if (!state.picaServiceId) {
                throw new Error('PicaService ID not set')
            }

            commit('setLoading', true)

            try {
                const data = await service.fetchFeatures(state.picaServiceId)
                commit('setFeatures', data || {})
            } finally {
                commit('setLoading', false)
            }
        },

        /**
         * Enable a feature
         */
        async enableFeature({ commit, state }, featureKey) {
            if (!state.picaServiceId) {
                throw new Error('PicaService ID not set')
            }

            const data = await service.enableFeature(state.picaServiceId, featureKey)
            if (data[featureKey]) {
                commit('updateFeature', { featureKey, config: data[featureKey] })
            }
        },

        /**
         * Disable a feature
         */
        async disableFeature({ commit, state }, featureKey) {
            if (!state.picaServiceId) {
                throw new Error('PicaService ID not set')
            }

            const data = await service.disableFeature(state.picaServiceId, featureKey)
            if (data[featureKey] !== undefined) {
                commit('updateFeature', { featureKey, config: data[featureKey] })
            }
        },

        /**
         * Update all features at once
         */
        async updateFeatures({ commit, state }, features) {
            if (!state.picaServiceId) {
                throw new Error('PicaService ID not set')
            }

            const data = await service.updateFeatures(state.picaServiceId, features)
            commit('setFeatures', data || {})
        },

        /**
         * Fetch configurable roles for collaborator limits
         * @param {number|string|null} picaServiceId - optional, falls back to state.picaServiceId
         */
        async fetchConfigurableRoles({ commit, state }, picaServiceId) {
            const id = picaServiceId || state.picaServiceId
            const roles = await service.fetchConfigurableRoles(id)
            commit('setRoles', roles)
        }
    }
})
